// @flow
import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { media, SmartLink } from "@nested/ui";

import { Arrow } from "@nested/ui/icons";
import { getConfig } from "@nested/config";
import SubMenu from "./SubMenu";
import { NavLinks, NavLink } from "./NavLink";
import { DetailedMenuItem, MenuItem } from "./MenuItem";
import { CloseMenu } from "./Icons";
import { NavButton } from "./NavButton";

/**
 * These breakpoints are the width in pixels at which items from the main nav move
 * into the 'more' menu.
 */
const CUSTOMER_STORIES_BREAKPOINT = 1000;
const FOR_SALE_BREAKPOINT = 1100;
const FEES_BREAKPOINT = 1200;

const { ACCOUNT_LOGIN_URI } = getConfig();

/*
 * This is the styling for the white box which appears when
 * the menu button is clicked on mobile
 */
const MobileMenuStyle = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: 9px;
  right: 7px;
  background-color: white;
  border-radius: 2px;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  width: calc(100% - 14px);
  transform-origin: top right;
  transition: visibility 300ms, opacity 300ms, transform 300ms ease-out;
  transform: scale(0.95);
  ${media.tablet`
    display: none;
  `};
`;

// This is the styling for the links in the middle of the nav on desktop
const DesktopMenuStyle = styled.div`
  display: none;
  text-align: center;
  height: 100%;
  ${media.tablet`
    display: block;
  `};
`;

const DesktopOnly = styled.div`
  display: none;
  font-weight: ${({ theme }) => theme.typography.bold};
  ${media.tablet`
    display: block;
  `};
`;

const DownArrow = styled(Arrow)`
  width: 14px;
`;

const ToTheRight = styled.div`
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
`;

const TextWrapper = styled.div`
  ${media.tablet`
    margin-right: 100px;
  `}
`;

/*
 * The same Menu component appears in both the desktop and mobile navs, so is
 * duplicated in the DOM. The reason for this is that if we use the same
 * components for both, it causes some strange behaviours with the CSS
 * transitions when going between the desktop and mobile versions.
 */
const Menu = ({ navigationColour }) => (
  <React.Fragment>
    <TextWrapper>
      <NavLinks>
        <NavLink heading>
          How it works <DownArrow fill="#353F52" />
          <SubMenu>
            <DetailedMenuItem
              to="/how-it-works"
              title="The process"
              detail="Find out how selling your home with Nested works"
            />
            <DetailedMenuItem
              to="/advance"
              title="Move home chain free"
              detail="Negotiate a better price on your next home and move on your terms"
            />
            <DetailedMenuItem
              to="/how-we-value"
              title="Our valuation process"
              detail="See how we use smart tools to value your home accurately"
            />
            <DetailedMenuItem
              to="/agency"
              title="Our agency team"
              detail="Learn about our end-to-end support from trusted advisors"
            />
          </SubMenu>
        </NavLink>
        <NavLink show={CUSTOMER_STORIES_BREAKPOINT}>
          <Link to="/customer-stories">Customer stories</Link>
        </NavLink>
        <NavLink show={FOR_SALE_BREAKPOINT}>
          <Link to="/for-sale">For sale</Link>
        </NavLink>
        <NavLink show={FEES_BREAKPOINT}>
          <Link to="/fees">Fees</Link>
        </NavLink>
      </NavLinks>
      <NavLinks>
        <NavLink show={media.tablet}>
          <SmartLink to={ACCOUNT_LOGIN_URI}>Sign In</SmartLink>
        </NavLink>
      </NavLinks>
      <NavLinks more>
        <NavLink heading>
          <DesktopOnly>
            More <DownArrow />
          </DesktopOnly>
          <SubMenu>
            <MenuItem
              hide={CUSTOMER_STORIES_BREAKPOINT}
              to="/customer-stories"
              title="Customer stories"
            />
            <MenuItem
              hide={FOR_SALE_BREAKPOINT}
              to="/for-sale"
              title="For sale"
            />
            <MenuItem hide={FEES_BREAKPOINT} to="/fees" title="Fees" />
            <MenuItem to="/about" title="About Nested" />
            <MenuItem to="/buying-advice" title="Buying Advice" />
            <MenuItem to="/careers" title="Careers" />
            <MenuItem to="/blog" title="Blog" />
            <MenuItem to="/press" title="Press" />
            <MenuItem to="/referrals" title="Refer a friend" />
          </SubMenu>
        </NavLink>
      </NavLinks>
      <DesktopOnly>
        <ToTheRight>
          <NavLinks>
            <NavButton colour={navigationColour}>
              <SmartLink to={ACCOUNT_LOGIN_URI}>Sign In</SmartLink>
            </NavButton>
          </NavLinks>
        </ToTheRight>
      </DesktopOnly>
    </TextWrapper>
  </React.Fragment>
);

type Props = {
  navigationColour: string,
};

export const MobileMenu = (props: Props) => (
  <MobileMenuStyle>
    <CloseMenu htmlFor="nav-toggle" />
    <Menu {...props} />
  </MobileMenuStyle>
);

export const DesktopMenu = (props: Props) => (
  <DesktopMenuStyle data-test="desktop-nav">
    <Menu {...props} />
  </DesktopMenuStyle>
);
